/**
 * Format a date as relative time (e.g., "2 hours ago", "3 days left").
 */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Returns a human-readable relative time string for a past date.
 * E.g., "just now", "5 minutes ago", "2 hours ago", "3 days ago".
 */
export function formatTimeAgo(dateString: string | null | undefined): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Never';

  const now = Date.now();
  const diff = now - date.getTime();

  if (diff < MINUTE) return 'Just now';
  if (diff < HOUR) {
    const minutes = Math.floor(diff / MINUTE);
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  if (diff < DAY) {
    const hours = Math.floor(diff / HOUR);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  const days = Math.floor(diff / DAY);
  if (days < 30) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

/**
 * Returns a human-readable string for time remaining until a future date.
 * E.g., "3 days left", "2 hours left", "Expired".
 */
export function formatTimeRemaining(dateString: string | null | undefined): string {
  if (!dateString) return 'Unknown';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Unknown';

  const now = Date.now();
  const diff = date.getTime() - now;

  if (diff <= 0) return 'Expired';

  if (diff < HOUR) {
    const minutes = Math.floor(diff / MINUTE);
    return `${minutes} minute${minutes === 1 ? '' : 's'} left`;
  }
  if (diff < DAY) {
    const hours = Math.floor(diff / HOUR);
    return `${hours} hour${hours === 1 ? '' : 's'} left`;
  }

  const days = Math.floor(diff / DAY);
  return `${days} day${days === 1 ? '' : 's'} left`;
}

/**
 * Returns the number of days remaining until a given date.
 * Returns negative values for past dates.
 */
export function getDaysRemaining(dateString: string | null | undefined): number | null {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;

  return Math.floor((date.getTime() - Date.now()) / DAY);
}
